import { getI18n } from "../assets/js/i18n/index.js";

class RouteOptions extends HTMLElement {
  set route(value) {
    this._route = value;
    if (this.isConnected) this.render();
  }

  get route() {
    return this._route;
  }

  set settingsOpen(value) {
    this._settingsOpen = Boolean(value);
    if (this.isConnected) this.render();
  }

  get settingsOpen() {
    return Boolean(this._settingsOpen);
  }

  connectedCallback() {
    this.render();
  }

  closeSettings() {
    if (!this._settingsOpen) return;
    this._settingsOpen = false;
    this.render();
  }

  emit(name, detail = {}) {
    this.dispatchEvent(
      new CustomEvent(name, {
        bubbles: true,
        detail: { id: this.route.id, ...detail },
      }),
    );
  }

  updateStyle(stylePatch, { render = false } = {}) {
    this._route = {
      ...this.route,
      style: {
        ...this.route.style,
        ...stylePatch,
      },
    };
    this.emit("sportsmap-route-change", { style: stylePatch });
    if (render) this.render();
  }

  render() {
    if (!this.route) return;

    const { t } = getI18n();
    const { name, style } = this.route;
    this.className = "card sports-route-options my-2";
    this.innerHTML = /* html */ `
      <div>
        <div class="d-flex align-items-center gap-2 p-3">
          <i class="fas fa-route" style="color:${style.color}"></i>
          <strong class="text-truncate" title="${this.escape(name)}">${this.escape(name)}</strong>
          <button type="button" class="btn btn-sm border ms-auto js-toggle-route-settings ${this._settingsOpen ? "active" : ""}" title="${t("sportsmap.routeOptions.settings")}" aria-label="${t("sportsmap.routeOptions.settings")}" aria-expanded="${this._settingsOpen ? "true" : "false"}">
            <i class="fas fa-cog"></i>
          </button>
          <button type="button" class="btn btn-sm border js-confirm-route-remove" title="${t("sportsmap.routeOptions.remove")}" aria-expanded="${this._confirming ? "true" : "false"}">
            <i class="fas fa-trash"></i>
          </button>
        </div>
        <div class="p-3 bg-light align-items-center ${this._settingsOpen && !this._confirming ? "" : "d-none"}">
          <div class="d-flex align-items-center flex-wrap mb-2">
            <input type="number" class="form-control me-2 js-route-option" data-key="thickness" value="${style.thickness}" min="1" max="18" style="width:4em" aria-label="${t("sportsmap.routeOptions.thickness")}" />
            <small>${t("sportsmap.routeOptions.thickness")}</small>
          </div>
          <div class="d-flex align-items-center flex-wrap mb-2">
            <input type="color" class="form-control form-control-color me-2 js-route-option" data-key="color" value="${style.color}" aria-label="${t("sportsmap.routeOptions.routeColor")}" />
            <small>${t("sportsmap.routeOptions.routeColor")}</small>
          </div>
          <div class="d-flex align-items-center flex-wrap mb-2">
            <input type="color" class="form-control form-control-color me-2 js-route-option" data-key="startColor" value="${style.startColor}" aria-label="${t("sportsmap.routeOptions.startColor")}" ${style.showStartPoint ? "" : "disabled"} />
            <small>${t("sportsmap.routeOptions.startColor")}</small>
            <div class="form-check form-switch mt-2 ms-auto">
              <input class="form-check-input js-route-point-toggle" data-key="showStartPoint" type="checkbox" role="switch" aria-label="${t("sportsmap.routeOptions.showStart")}" ${style.showStartPoint ? "checked" : ""}>
            </div>
          </div>
          <div class="d-flex align-items-center flex-wrap mb-2">
            <input type="color" class="form-control form-control-color me-2 js-route-option" data-key="endColor" value="${style.endColor}" aria-label="${t("sportsmap.routeOptions.endColor")}" ${style.showEndPoint ? "" : "disabled"} />
            <small>${t("sportsmap.routeOptions.endColor")}</small>
            <div class="form-check form-switch mt-2 ms-auto">
              <input class="form-check-input js-route-point-toggle" data-key="showEndPoint" type="checkbox" role="switch" aria-label="${t("sportsmap.routeOptions.showEnd")}" ${style.showEndPoint ? "checked" : ""}>
            </div>
          </div>
        </div>
      </div>
      ${
        this._confirming
          ? /* html */ `
            <div class="route-remove-confirmation bg-light p-3">
              <p><small>${t("sportsmap.routeOptions.removeConfirmation")}</small></p>
              <div class="d-flex align-items-center gap-2">
                <button type="button" class="btn btn-danger btn-sm js-remove-route">${t("sportsmap.routeOptions.confirmRemove")}</button>
                <button type="button" class="btn btn-outline-dark btn-sm js-cancel-route-remove">${t("sportsmap.routeOptions.cancelRemove")}</button>
              </div>
            </div>
          `
          : ""
      }
    `;

    this.querySelectorAll(".js-route-option").forEach((input) => {
      input.addEventListener("input", () => {
        const key = input.dataset.key;
        const value = key === "thickness" ? Number(input.value) : input.value;
        this.updateStyle({ [key]: value });
      });
    });

    this.querySelectorAll(".js-route-point-toggle").forEach((toggle) => {
      toggle.addEventListener("change", (event) => {
        this.updateStyle(
          { [event.target.dataset.key]: event.target.checked },
          { render: true },
        );
      });
    });

    this.querySelector(".js-toggle-route-settings")?.addEventListener(
      "click",
      () => {
        const willOpen = !this._settingsOpen;

        if (willOpen) {
          document.querySelectorAll("route-options").forEach((element) => {
            if (element !== this) element.closeSettings();
          });
        }

        this._settingsOpen = willOpen;
        this._confirming = false;
        this.render();
      },
    );

    this.querySelector(".js-confirm-route-remove")?.addEventListener(
      "click",
      () => {
        this._confirming = true;
        this.render();
      },
    );

    this.querySelector(".js-cancel-route-remove")?.addEventListener(
      "click",
      () => {
        this._confirming = false;
        this.render();
      },
    );

    this.querySelector(".js-remove-route")?.addEventListener("click", () => {
      this.emit("sportsmap-route-remove");
    });
  }

  escape(value) {
    const element = document.createElement("span");
    element.textContent = value || getI18n().t("sportsmap.genericRouteName");
    return element.innerHTML;
  }
}

if (!customElements.get("route-options")) {
  customElements.define("route-options", RouteOptions);
}
